import { LOAD_PIXEL_SERVER } from 'constants/reduxActions';
import { postMartyPixel } from 'apis/martyPixel';
import { getPageLang, getPageTitle } from 'helpers/ClientUtils';
import { fetchMartyPixelErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { middlewareTrack } from 'apis/amethyst';
import { evPixelBrandEvent, evPixelCartEvent, evPixelEvent, evPixelOrderEvent, evPixelPdpEvent, evPixelSearchEvent } from 'events/pixel';
import logger from 'middleware/logger';

/**
 * Invokes the pixel server with the data given via arguments plus some global
 * stuff.
 *
 * @param {String} pageType
 * the "pageType" key from the pixel bucket
 *
 * @param {Object} [trackingPayload={}]
 * additional data to post to the server for this pageType
 *
 * @param {String} [pageId='']
 * an additional identifer when combined with pageType to use when determining
 * when to fire the pixel server message. For instance to identify client
 * routing between different product pages. (not sent to pixel server)
 */
export function firePixelServer(pageType, trackingPayload = {}, pageId = '') {
  return function (dispatch, getState) {
    const action = makePixelServerAction(getState(), pageType, trackingPayload, pageId);
    dispatch(action);
  };
}

export const fireGTMEvent = (pageType, trackingPayload, sessionId = '') => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: pageType,
      sessionId: sessionId,
      ...trackingPayload
    });
  }
};

export function firePixelEvent(pageType, trackingPayload = {}, pageId = '') {
  const pageLang = getPageLang();
  const pageTitle = getPageTitle();
  trackPixelEvent(pageType, pageId, pageLang, pageTitle, trackingPayload);
}

export function makePixelServerAction(state, pageType, trackingPayload = {}, pageId = '') {
  const {
    router: {
      location: { search }
    },
    cookies
  } = state;
  // data.pageId field is used to "cache bust" for pixel pages with no
  // parameters and the same pageType (e.g landing pages).
  const pageLang = getPageLang();
  const pageTitle = getPageTitle();
  const sessionId = cookies['session-id'];
  // Sending pixel events to amethyst
  trackPixelEvent(pageType, pageId, pageLang, pageTitle, trackingPayload);
  // Sending pixel events to GTM
  fireGTMEvent(pageType, trackingPayload, sessionId);

  return {
    type: LOAD_PIXEL_SERVER,
    pageType,
    data: {
      customerCountryCode: extractCustomerCountryCode(cookies),
      pageId,
      pageLang,
      pageTitle,
      sessionId,
      ...trackingPayload
    },
    queryString: search
  };
}

export function extractCustomerCountryCode(cookies) {
  return cookies.geo?.split('/')[0] || null;
}

export function fireMartyPixel(qs, type, endpoints = { postMartyPixel }) {
  return function () {
    return endpoints
      .postMartyPixel(`type=${type}&${qs}`)
      .then(fetchMartyPixelErrorMiddleware)
      .catch(() => {
        logger('Unable to get valid response from /martypixel endpoint, not firing marty pixel server.');
      });
  };
}

export function trackPixelEvent(pageType, pageId, pageLang, pageTitle, trackingPayload = {}) {
  const event = { pageType, pageId, pageLang, pageTitle, trackingPayload };
  fetchEventTypeForPayload(pageType, event, {
    evPixelOrderEvent,
    evPixelBrandEvent,
    evPixelPdpEvent,
    evPixelSearchEvent,
    evPixelCartEvent,
    evPixelEvent
  });
}

export function modifyOrderConfirmationEvent(orderEvent) {
  const updatedOrders = [];
  orderEvent.trackingPayload?.orders.forEach(order => {
    const { billingAddress, shippingAddress } = order;
    const orderObj = {
      ...order,
      billingAddress: {
        ...billingAddress,
        postalCode: billingAddress?.postalCode.slice(0, 5)
      },
      shippingAddress: {
        ...shippingAddress,
        postalCode: shippingAddress?.postalCode.slice(0, 5)
      }
    };
    updatedOrders.push(orderObj);
  });
  const { billingAddress, shippingAddress } = orderEvent?.trackingPayload?.order;
  const event = {
    ...orderEvent,
    trackingPayload: {
      order: {
        ...orderEvent.trackingPayload.order,
        billingAddress: {
          ...billingAddress,
          postalCode: billingAddress?.postalCode.slice(0, 5)
        },
        shippingAddress: {
          ...shippingAddress,
          postalCode: shippingAddress?.postalCode.slice(0, 5)
        }
      },
      orders: updatedOrders
    }
  };
  return event;
}

export function fetchEventTypeForPayload(
  pageType,
  event = {},
  { evPixelOrderEvent, evPixelBrandEvent, evPixelPdpEvent, evPixelSearchEvent, evPixelCartEvent, evPixelEvent }
) {
  switch (pageType) {
    case 'confirmation':
      middlewareTrack(evPixelOrderEvent(modifyOrderConfirmationEvent(event)));
      break;
    case 'brand':
      middlewareTrack(evPixelBrandEvent(event));
      break;
    case 'pdp':
      middlewareTrack(evPixelPdpEvent(event));
      break;
    case 'search':
      middlewareTrack(evPixelSearchEvent(event));
      break;
    case 'cart':
      middlewareTrack(evPixelCartEvent(event));
      break;
    default:
      middlewareTrack(evPixelEvent(event));
  }
}
